import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Stack } from 'src/design/atoms/Stack';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DesignModelDto, JobDto } from 'src/api/FiretrolApiClient';
import { useDesignModel } from 'src/api/queries/designModels/useDesignModel';
import { getStatusCode } from 'src/api/queries/errorClassification';
import { useJobs } from 'src/hooks/useJobs';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { EmptyState } from 'src/design/atoms/EmptyState';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { Header } from 'src/design/organisms/Header';
import { LineSummaryPanel } from 'src/routes/Jobs/DesignModels/components/LineSummaryPanel';
import { ProcessingWarningsPanel } from 'src/routes/Jobs/DesignModels/components/ProcessingWarningsPanel';
import { DesignModelForm } from 'src/routes/Jobs/DesignModels/components/DesignModelForm';
import { useLineGrouping } from 'src/routes/Jobs/DesignModels/hooks/useLineGrouping';

const NEW_MODEL_TITLE = 'New Design Model';

/** A fetch that succeeded without a model counts as not found too. */
const isNotFound = (error: unknown) =>
  error == null || getStatusCode(error) === 404;

const emptyModel = (jobId: number): DesignModelDto => ({
  name: '',
  jobId,
  fileSize: 0,
  missingFileTypes: [],
});

const DesignModelHeader: React.FC<{ job: JobDto; title: string }> = ({
  job,
  title,
}) => (
  <Header
    title={title}
    breadcrumbProps={{
      items: [
        { text: 'Jobs', href: '/jobs' },
        {
          text: `${job.name} - ${job.jobNumber}`,
          href: `/jobs/${job.id}/summary`,
        },
        { text: 'Design Models', href: `/jobs/${job.id}/design-models` },
        { text: title },
      ],
    }}
    helpText="View and edit design model details for the progress tracker tool"
  />
);

export const ViewDesignModel: React.FC = () => {
  const { designModelId: routeId } = useParams<{ designModelId: string }>();
  const navigate = useNavigate();
  const { selectedJob } = useJobs();
  const isCreateMode = routeId === 'create';
  const designModelId = isCreateMode ? undefined : routeId;

  const detailQuery = useDesignModel(designModelId);
  const { data: designModel, isLoading } = detailQuery;
  const lineGroupingQuery = useLineGrouping(designModelId);

  if (!selectedJob) return null;
  const listPath = `/jobs/${selectedJob.id}/design-models`;

  if (!isCreateMode && isLoading)
    return (
      <Group justify="center" mt="xl">
        <Loader size="lg" />
      </Group>
    );

  if (!isCreateMode && !designModel && !isNotFound(detailQuery.error))
    return (
      <>
        <DesignModelHeader job={selectedJob} title="Design model" />
        <Stack align="center" gap="md">
          <EmptyState
            header="Couldn't load the design model"
            description="Check your connection and try again."
          />
          <Group>
            <SecondaryButton onClick={() => navigate(listPath)}>
              Back to design models
            </SecondaryButton>
            <BasicButton
              loading={detailQuery.isFetching}
              onClick={() => detailQuery.refetch()}
            >
              Retry
            </BasicButton>
          </Group>
        </Stack>
      </>
    );

  if (!isCreateMode && !designModel)
    return (
      <>
        <DesignModelHeader job={selectedJob} title="Design model not found" />
        <Stack align="center" gap="md">
          <EmptyState
            header="Design model not found"
            description="It may have been deleted, or you may not have access."
          />
          <SecondaryButton onClick={() => navigate(listPath)}>
            Back to design models
          </SecondaryButton>
        </Stack>
      </>
    );

  return (
    <>
      <DesignModelHeader
        job={selectedJob}
        title={
          isCreateMode ? NEW_MODEL_TITLE : designModel?.name || 'Design model'
        }
      />
      <DesignModelForm
        key={designModelId ?? 'create'}
        initialValues={designModel ?? emptyModel(selectedJob.id)}
        selectedJob={selectedJob}
        onCreated={(created) => navigate(`${listPath}/${created.id}`)}
        onDeleted={() => navigate(listPath)}
        onCancel={() => navigate(listPath)}
        diagnostics={
          !isCreateMode && (
            <>
              <ProcessingWarningsPanel
                grouping={lineGroupingQuery.data}
                isError={lineGroupingQuery.isError}
                onRetry={() => lineGroupingQuery.refetch()}
              />
              <LineSummaryPanel grouping={lineGroupingQuery.data} />
            </>
          )
        }
      />
    </>
  );
};
